import { Typography, useTheme } from '@mui/material';
import { truncateWithEllipsis } from '@smartfm/container/utils/strings';
import { clsx } from 'clsx';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { Box, type BoxProps } from '../Box';
import { Divider } from '../Divider';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Input, type InputProps } from '../Input';
import { ListItem, type ListItemProps } from '../ListItem';
import { Menu, type MenuProps } from '../Menu';
import { ProgressBar } from '../ProgressBar';
import { Stack } from '../Stack';
import './styles.scss';

const itemsListMaxHeight = 314;
const searchInputMaxLength = 100;
const scrollToItemIndexThreshold = 4;

function ProgressBarElement() {
  return (
    <Box sx={{ position: 'absolute', width: '100%', zIndex: 1 }}>
      <ProgressBar determinate={false} />
    </Box>
  );
}

function CheckIconElement() {
  const theme = useTheme();
  return (
    /* 
      TODO: Box with color="primary.main" is a temporary workaround to fix the color of the icon.
      Should be revised after Emerald System is updated.
    */
    <Box
      sx={{
        color: 'primary.main',
        gridArea: '1/1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 52,
      }}
    >
      <Icon
        icon='check'
        type='rounded'
        color={theme.palette.primary.light}
      />
    </Box>
  );
}

function BodyContainer(props: BoxProps) {
  const { sx, ...otherProps } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...sx,
      }}
      {...otherProps}
    />
  );
}

// Base props without the conditional properties
interface BodyProps {
  width: number;
  itemHeight: number;
  values: string[];
  itemsMap: Record<string, Omit<ListItemProps, 'label'> & { label: string }>;
  itemsOrder: string[];
  favoriteItems: string[];
  onValueChange(value: string): void;
  onFavoriteChange(value: string): void;
  stickyListItem?: ReactNode;
  hasSearchValue?: boolean;
  isLoading?: boolean;
  virtualListRef?: React.RefObject<FixedSizeList>;
  isMenuContentVisible?: boolean;
  onDefaultChange?(value: string): void;
}

function Body(props: BodyProps) {
  const {
    width,
    itemHeight,
    values,
    itemsMap,
    itemsOrder,
    favoriteItems,
    onValueChange,
    onFavoriteChange,
    stickyListItem,
    hasSearchValue,
    isLoading,
    onDefaultChange,
    virtualListRef,
    isMenuContentVisible = true,
  } = props;

  const totalItemsHeight = itemHeight * itemsOrder.length;
  const containerHeight = Math.min(itemsListMaxHeight, totalItemsHeight);

  function handleItemClick(
    ...[, item]: Parameters<NonNullable<ListItemProps['onClick']>>
  ) {
    onValueChange(item.value);
  }

  function renderListItem(value: string, item: ListItemProps) {
    const isFavorite = favoriteItems.includes(value);
    const isActive = values.includes(value);
    const { label, secondaryText, ...otherProps } = item;

    // Limit the label for one line when there is secondary text
    const maxLabelLength = (secondaryText ? 40 : 80) / (width > 400 ? 1 : 2);
    const formattedLabel = label && truncateWithEllipsis(label, maxLabelLength);

    return (
      <Box
        title={label}
        sx={{
          flexGrow: 1,
          display: 'grid',
          height: itemHeight,
          background: 'white',
        }}
      >
        {isActive && <CheckIconElement />}
        <ListItem
          classes='search-menu__list-item'
          onClick={handleItemClick}
          value={value}
          trailingTemplate={
            /* 
              TODO: Box with color="primary.main" is a temporary workaround to fix the color of the icon.
              Should be revised after Emerald System is updated.
            */
            <Box
              color='primary.main'
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {onDefaultChange && (
                <IconButton
                  type='Icon'
                  size='Medium'
                  shape='Circle'
                  icon='home'
                  title='Set as default'
                  iconFillType='rounded'
                  className='search-menu__action-button'
                  onClick={event => {
                    event.stopPropagation();
                    onDefaultChange?.(value);
                  }}
                />
              )}
              <IconButton
                type='Icon'
                size='Medium'
                shape='Circle'
                icon='star'
                title={
                  isFavorite ? 'Remove from favorites' : 'Add to favorites'
                }
                iconFillType='rounded'
                className={clsx('search-menu__action-button', {
                  'search-menu__action-button--active': isFavorite,
                })}
                onClick={event => {
                  event.stopPropagation();
                  onFavoriteChange(value);
                }}
              />
            </Box>
          }
          label={formattedLabel}
          secondaryText={
            secondaryText && truncateWithEllipsis(secondaryText, 100)
          }
          {...otherProps}
        />
      </Box>
    );
  }

  // Show a message when there are no search results
  if (hasSearchValue && itemsOrder.length === 0) {
    return (
      <>
        {stickyListItem}
        {isLoading && <ProgressBarElement />}
        <BodyContainer sx={{ height: itemsListMaxHeight }}>
          <Typography
            color='textSecondary'
            textAlign='center'
          >
            No results found. <br />
            Please try another search.
          </Typography>
        </BodyContainer>
      </>
    );
  }

  return (
    <>
      {stickyListItem}
      {isLoading && <ProgressBarElement />}
      <Box
        sx={{
          opacity: isMenuContentVisible ? 1 : 0,
          transition: 'opacity 0.1s ease-in-out',
        }}
      >
        <FixedSizeList
          ref={virtualListRef}
          height={containerHeight}
          width={width}
          itemCount={itemsOrder.length}
          itemSize={itemHeight}
        >
          {({ index, style }) => {
            const value = itemsOrder[index];

            return (
              <Box
                id={value}
                key={value}
                sx={{
                  ...style,
                  display: 'flex',
                }}
                tabIndex={0} // Make the item focusable
              >
                {renderListItem(value, itemsMap[value])}
              </Box>
            );
          }}
        </FixedSizeList>
      </Box>
    </>
  );
}
export interface SearchMenuProps
  extends Omit<BodyProps, 'stickyListItem' | 'onFavoriteChange'>,
    Pick<
      MenuProps,
      'opened' | 'triggerElement' | 'position' | 'onClose' | 'onOpen'
    > {
  values: string[];
  triggerElement: NonNullable<MenuProps['triggerElement']>;
  stickyListItemProps?: Omit<ListItemProps, 'classes'> & {
    isActive: boolean;
  };
  searchValue: InputProps['value'];
  onSearchChange(searchValue: string): void;
  onFavoriteChange(value: string): void;
  onFavoriteFilterChange(isEnabled: boolean): void;
  isFavoriteFilterActive: boolean;
  favoriteItems: string[];
  trailingHeaderElement?: ReactNode;
  defaultItem?: string;
}

export function SearchMenu(props: SearchMenuProps) {
  const {
    width,
    itemHeight,
    opened,
    onOpen,
    onClose,
    position,
    triggerElement,
    values,
    itemsMap,
    itemsOrder,
    searchValue,
    onValueChange,
    onSearchChange,
    onFavoriteChange,
    onFavoriteFilterChange,
    stickyListItemProps,
    trailingHeaderElement,
    isFavoriteFilterActive,
    favoriteItems,
    isLoading,
    defaultItem,
    onDefaultChange,
  } = props;

  const virtualListRef = useRef<FixedSizeList>(null);

  const hasFavorites = favoriteItems.length > 0;
  const currentItemsOrder = useMemo(() => {
    return itemsOrder.filter(value => {
      // Always exclude defaultItem if it exists
      if (defaultItem && value === defaultItem) return false;
      // Filter for favorites when favorites filter is active
      if (isFavoriteFilterActive) return favoriteItems.includes(value);
      // Include all non-default items when favorites filter is inactive
      return true;
    });
  }, [itemsOrder, favoriteItems, isFavoriteFilterActive, defaultItem]);

  // Find the index of the selected item
  const selectedIndex = currentItemsOrder.findIndex(item =>
    values.includes(item)
  );

  const isMenuContentVisibleByDefault =
    values.length === 0 ||
    (typeof defaultItem === 'string' && values[0] === defaultItem) ||
    selectedIndex < scrollToItemIndexThreshold;

  const [isMenuContentVisible, setIsMenuContentVisible] = useState(
    isMenuContentVisibleByDefault
  );

  // Scroll to selected item when menu opens
  function handleMenuOpen(event: React.SyntheticEvent) {
    // Call the original onOpen handler
    onOpen?.(event);

    if (isMenuContentVisibleByDefault) {
      // If there's nothing to scroll to, just show the content
      setIsMenuContentVisible(true);
      return;
    }

    // Set content as hidden initially
    setIsMenuContentVisible(false);

    // First, scroll while content is hidden
    virtualListRef.current?.scrollToItem(selectedIndex, 'smart');

    // After scrolling, make content visible and focus the item
    setTimeout(() => {
      setIsMenuContentVisible(true);

      // Focus the element after making content visible
      setTimeout(() => document.getElementById(values[0])?.focus(), 50);
    }, 0);
  }

  function handleFilterFavoritesClick() {
    onFavoriteFilterChange(!isFavoriteFilterActive);
  }

  // Reset visibility state when menu closes
  function handleMenuClose(event: React.SyntheticEvent) {
    onClose?.(event);
    setIsMenuContentVisible(isMenuContentVisibleByDefault);
  }

  return (
    <Menu
      triggerElement={triggerElement}
      position={position}
      // This prop is exposed for Storybook testing only
      opened={opened}
      onClose={handleMenuClose}
      onOpen={handleMenuOpen}
    >
      <Box
        sx={{
          maxHeight: 448,
          overflow: 'hidden',
          width,
        }}
      >
        <Stack
          direction='row'
          spacing={1.25}
          useFlexGap={true}
          sx={{ padding: theme => theme.spacing(1, 1.5, 1.5) }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Input
              placeholder='Search'
              trailingIcon='search'
              showClearIcon={true}
              maxLength={searchInputMaxLength}
              value={searchValue}
              onChange={(_, changedValue) => onSearchChange(changedValue)}
            />
          </Box>
          {/* 
            TODO: Box with color="primary.main" is a temporary workaround to fix the color of the icon.
            Should be revised after Emerald System is updated.
          */}
          <Box color='primary.main'>
            <IconButton
              icon='star'
              iconFillType='rounded'
              size='Large'
              type={isFavoriteFilterActive ? 'Secondary' : 'Light'}
              disabled={!hasFavorites}
              title={
                !hasFavorites
                  ? 'No favorites'
                  : isFavoriteFilterActive
                    ? 'Show all'
                    : 'Show favorites'
              }
              onClick={handleFilterFavoritesClick}
            />
          </Box>
          {trailingHeaderElement && (
            <Box color='primary.main'>{trailingHeaderElement}</Box>
          )}
        </Stack>
        <Body
          virtualListRef={virtualListRef}
          width={width}
          itemHeight={itemHeight}
          values={values}
          itemsMap={itemsMap}
          itemsOrder={currentItemsOrder}
          favoriteItems={favoriteItems}
          isMenuContentVisible={isMenuContentVisible}
          stickyListItem={
            stickyListItemProps &&
            (({ isActive, ...otherProps }) => (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    height: itemHeight,
                  }}
                >
                  {isActive && <CheckIconElement />}
                  <ListItem
                    classes={clsx('search-menu__list-item', {
                      'search-menu__list-item--sticky-active': isActive,
                    })}
                    {...otherProps}
                  />
                </Box>
                <Divider />
              </>
            ))(stickyListItemProps)
          }
          isLoading={isLoading}
          hasSearchValue={Boolean(searchValue)}
          onValueChange={onValueChange}
          onFavoriteChange={onFavoriteChange}
          onDefaultChange={onDefaultChange}
        />
      </Box>
    </Menu>
  );
}
